import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Panel } from "@/components/ui/Panel";
import { useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { nestingActs } from "@/v2/figures/ComponentNesting";
import type {
  FigureListResponse,
  FigureResponse,
  SetAsideFigureResponse,
  WalkthroughClipResponse,
} from "@patent-studio/api-types";
import type { Definitions, FigureRow } from "@/lib/element-registry";
import { assessSet } from "@/lib/figure-status";
import { numeralsOf } from "@/lib/numbering";
import {
  canRemoveNumbering,
  useFigureDefinitions,
} from "@/hooks/use-figure-definitions";
import { useFigureBrief, useFigures } from "@/hooks/use-figures";
import { figuresKey } from "@/hooks/query-keys";
import { useWalkthroughClipUpload } from "@/hooks/use-walkthrough-clip-upload";
import { liftReason } from "@/hooks/use-lifted-confirmations";
import { retryFileProcessing } from "@/services/files";
import type { Placement } from "../figures/AnnotatedDrawing";
import type { MarkEdits } from "../figures/AnnotationCard";
import { FigureDefineSurface } from "../figures/FigureDefineSurface";
import { DEFINE_COLUMN_BOUND } from "../figures/define-layout";
import { cn } from "@/lib/utils";
import {
  DeletedNavList,
  SetAsideFigureView,
  announceDeleted,
  figureName,
  type SetAsideActsProps,
} from "../figures/DeletedFigures";
import { FigureNavigator } from "../figures/FigureNavigator";
import { FigureSettingsMenu } from "../figures/FigureSettingsMenu";
import { clipsHeldByFigure } from "../figures/walkthrough-clips";
import { StepLink } from "./StepLink";
import { stageHeadingLabel } from "@/v2/stage-vocabulary";
import { StagePage } from "../layout/StagePage";
import { arriveAt, type TermTarget, useTermArrival } from "../terms/term-route";
import { registryRowId } from "../figures/ElementRegistryRows";

/**
 * The conventions the set is drawn under, once there is a drawing to draw under them. They sit
 * above the figure list, the column that reaches every drawing: the figure's own heading would
 * make a change read as a change to that figure alone. The panel opens
 * rightward, over the page, and never inline in the column.
 */
function SetSettings({
  set,
  drawn,
}: {
  set: ReturnType<typeof useFigureDefinitions>;
  drawn: FigureResponse | undefined;
}) {
  if (drawn === undefined) return null;
  return (
    <FigureSettingsMenu
      align="start"
      settings={set.definitions.settings}
      onConnectorDefaults={set.setConnectorDefaults}
      onNumeralStyle={set.setNumeralStyle}
      onNumberingConvention={set.setNumberingConvention}
    />
  );
}

/** What the page says while the set holds no figure to work on. */
function NothingToDefine() {
  return (
    <p className="mt-6 text-sm text-muted-foreground">
      No figures to define yet. Upload and arrange your drawings first.
    </p>
  );
}

/** The way back, and the way on once there is somewhere to go. */
function StepLinks({ taskId }: { taskId: string | undefined }) {
  return (
    <div className="mt-4 flex justify-between">
      <StepLink taskId={taskId} from="define" direction="previous" />
      <StepLink taskId={taskId} from="define" direction="next" primary />
    </div>
  );
}

/**
 * Which drawing is open, and which pin on it is selected.
 *
 * One hook because the two are one idea with a rule between them: a pin belongs to the
 * drawing it sits on, so choosing another drawing drops the selection. Held apart from them
 * and that rule has to be remembered at every call site that moves between drawings.
 *
 * The chosen drawing falls back to the first, so a reload and a deleted drawing both land
 * somewhere real rather than on a blank surface.
 */
function useDefineFocus(figures: FigureResponse[]) {
  // The review screen's Edit Figure names its figure in the URL; read once as
  // the initial choice so later navigation inside the pane wins over the URL.
  const [params] = useSearchParams();
  const [chosen, setChosen] = useState<string | undefined>(
    params.get("figure") ?? undefined,
  );
  const [selected, setSelected] = useState<Placement | undefined>(undefined);
  // A deleted figure opened from the navigator's fold, shown in place of the chosen one.
  const [asideId, setAsideId] = useState<string | undefined>(undefined);

  // `at` rather than an index, which types as always present because this project does not
  // check indexed access. The empty case is one the caller has to render, so the type has to
  // admit it.
  const showing =
    figures.find((figure) => figure.file_id === chosen) ?? figures.at(0);

  // The first drawing shown is pinned as the choice the moment there is one, rather than
  // left as a fallback to whatever the listing leads with. A reorder from the navigator
  // rewrites the listing's order optimistically, and an unpinned fallback would carry the
  // working surface off to whichever figure the drag put first. The fallback stays for the
  // case it was written for: the chosen drawing leaving the listing altogether.
  // Adjusted during render, as the arrangement drag adjusts its lanes: the pin has to be
  // in place on the same pass that first draws the figure, not an effect later.
  const first = figures.at(0)?.file_id;
  if (chosen === undefined && first !== undefined) setChosen(first);

  const open = (fileId: string) => {
    setSelected(undefined);
    setAsideId(undefined);
    setChosen(fileId);
  };
  const openAside = (fileId: string | undefined) => {
    setSelected(undefined);
    setAsideId(fileId);
  };

  return {
    showing,
    open,
    asideId,
    openAside,
    picked: selected,
    select: setSelected,
  };
}

/**
 * The selection as the set now describes it, or nothing.
 *
 * Looked up by id on every read rather than kept in step by hand. A pin can leave the set
 * underneath the attorney, by a removal or by a merge collapsing it onto a mark already at
 * that point, and it can change element without moving. Deriving both from the set means a
 * selection can never name a pin the drawing does not draw, and never carries an element the
 * pin has stopped belonging to.
 */
function livePlacement(
  definitions: Definitions,
  fileId: string | undefined,
  picked: Placement | undefined,
): Placement | undefined {
  if (picked === undefined) return undefined;
  const here = definitions.figures.find((row) => row.file_id === fileId);
  const live = here?.instances?.find((i) => i.id === picked.id);
  return live === undefined
    ? undefined
    : { id: live.id, elementId: live.element_id };
}

/**
 * Every way one mark changes, bound to the drawing that is open.
 *
 * Gathered here rather than spelled out in the surface below it, because the drawing is the
 * only thing the controls do not already know and binding it is all these do.
 */
function markEdits(
  set: ReturnType<typeof useFigureDefinitions>,
  fileId: string,
): MarkEdits {
  return {
    line: (id, kind) => {
      set.setLineKind(fileId, { id, kind });
    },
    arrowhead: (id, on) => {
      set.setArrowhead(fileId, { id, on });
    },
    movePin: (id, pin) => {
      set.movePin(fileId, { id, pin });
    },
    aimTip: (id, tip) => {
      set.aimTip(fileId, { id, tip });
    },
    bend: (id, index, control) => {
      set.bend(fileId, { id, index, control });
    },
    numeralStyle: (id, style) => {
      set.setMarkNumeralStyle(fileId, id, style);
    },
  };
}

/** Each component's title as the store has acknowledged it. */
function titlesOf(served: Definitions): ReadonlyMap<string, string> {
  return new Map(served.registry.map((row) => [row.element_id, row.title]));
}

/**
 * The walk-through slice of the surface's props, bundled where the pane
 * decides what each act means. Saving persists. Submitting only has anything
 * left to say when the press stored nothing new (the card has already flushed
 * by the time it runs); otherwise it is indistinguishable from a button that
 * failed. Both capture paths land the clip on the open figure.
 */
/* The clips the open figure holds, pending uploads folded beside the served. */
function heldClips(
  data: { walkthrough_clips?: WalkthroughClipResponse[] | null },
  pendingClips: readonly WalkthroughClipResponse[],
  definition: FigureRow | undefined,
) {
  return clipsHeldByFigure(
    [...(data.walkthrough_clips ?? []), ...pendingClips],
    definition?.walkthrough_file_ids,
    definition?.walkthrough_adopted_clip_ids,
  );
}

function walkthroughActs(
  set: ReturnType<typeof useFigureDefinitions>,
  fileId: string,
  uploadClip: ReturnType<typeof useWalkthroughClipUpload>["send"],
  retryClip: (clipId: string) => void,
  clips: readonly WalkthroughClipResponse[] | null | undefined,
) {
  // The self-saving fields take the acknowledged row, so a refused write leaves
  // the attorney's typing on screen for the next flush to send again.
  const stored = set.served.figures.find((row) => row.file_id === fileId);
  return {
    walkthroughLifted: liftReason(set.lifted.get(fileId), clips ?? []),
    onRetryClip: retryClip,
    walkthrough: stored?.walkthrough ?? "",
    onWalkthrough: (walkthrough: string) =>
      set.describeWalkthrough(fileId, walkthrough),
    description: stored?.description ?? "",
    onDescription: (description: string) =>
      set.describeFigure(fileId, description),
    onAccept: (walkthrough: string) => {
      set.acceptWalkthrough(fileId, true, walkthrough);
    },
    onAmend: () => {
      set.acceptWalkthrough(fileId, false);
    },
    onUploadClip: (file: File) => {
      void uploadClip(fileId, file);
    },
    onRecordClip: (file: File) => {
      void uploadClip(fileId, file, "recording");
    },
    onAdoptTranscript: (clipId: string, transcript: string) => {
      set.adoptTranscript(fileId, clipId, transcript);
    },
  };
}

/**
 * Ask the server to run a failed clip's transcription again, then re-read the
 * figures so the row leaves its failed state. The outcome itself arrives on
 * the file-status stream like any other run.
 */
function useClipTranscription(taskId: string | undefined) {
  const qc = useQueryClient();
  return (clipId: string) => {
    if (taskId === undefined) return;
    retryFileProcessing(taskId, clipId)
      .then(() => qc.invalidateQueries({ queryKey: figuresKey(taskId) }))
      .catch(() => {
        toast.error("Could not retry the transcription. Please try again.");
      });
  };
}

/** The title of the group a figure sits in, which its brief description's suggestion names. */
function groupTitleOf(
  layout: FigureListResponse["layout"] | undefined,
  fileId: string,
): string | null {
  for (const entry of layout?.entries ?? []) {
    if (
      entry.kind === "group" &&
      (entry.members ?? []).some((member) => member.file_id === fileId)
    )
      return entry.title;
  }
  return null;
}

/* The acts on the open figure that need nothing beyond which figure it is. */
function figureActs(
  set: ReturnType<typeof useFigureDefinitions>,
  figure: FigureResponse,
) {
  const fileId = figure.file_id;
  return {
    onAddNumbering: () => {
      set.addNumbering(fileId);
    },
    onRemoveNumbering: () => {
      set.removeNumbering(fileId);
    },
    onPriorArt: (priorArt: boolean) => {
      set.markPriorArt(fileId, priorArt);
    },
    onConfirmAll: () => {
      set.confirmTitled(fileId);
    },
    // Nothing to do about which figure is open: the focus falls back to the
    // first one left as soon as the answered listing no longer holds this one.
    // Named before it goes, because after it its label is the next figure's.
    onDelete: () => {
      const name = figureName(figure.label, figure.original_filename);
      void set.deleteFigure(fileId).then((gone) => {
        if (gone) announceDeleted(name, () => set.restoreFigure(fileId));
      });
    },
  };
}

/** Restore opens the figure back in the set; Permanently Delete closes it if it was open. */
function setAsideActs(
  set: ReturnType<typeof useFigureDefinitions>,
  focus: ReturnType<typeof useDefineFocus>,
): SetAsideActsProps {
  return {
    onRestore: (figure: SetAsideFigureResponse) => {
      void set.restoreFigure(figure.file_id).then((back) => {
        if (back) focus.open(figure.file_id);
      });
    },
    onPurge: (figure: SetAsideFigureResponse) => {
      if (focus.asideId === figure.file_id) focus.openAside(undefined);
      void set.purgeFigure(figure.file_id);
    },
  };
}

/**
 * The column beside the working surface: the figures, then the set's settings under them,
 * as the design draws it.
 *
 * The tags are projected here from the in-flight definitions, never read off the served
 * rows, so the column moves with the attorney's typing rather than the server's next answer.
 */
function DefineSidebar({
  taskId,
  listing,
  definitions,
  selectedId,
  onSelect,
  asideId,
  onSelectAside,
  acts,
  head,
}: {
  taskId: string | undefined;
  listing: FigureListResponse;
  definitions: Definitions;
  selectedId: string;
  onSelect: (fileId: string) => void;
  asideId: string | undefined;
  onSelectAside: (fileId: string) => void;
  acts: SetAsideActsProps;
  /** What sits above the list card, on the row the figure's heading shares. */
  head: ReactNode;
}) {
  const figures = listing.figures ?? [];
  const layout = listing.layout ?? { entries: [], version: 0 };
  const { statuses } = useMemo(
    () =>
      assessSet(
        definitions,
        (listing.figures ?? []).map((figure) => figure.file_id),
      ),
    [definitions, listing.figures],
  );
  return (
    // The card is bounded to part of the screen and the list scrolls inside it, so a long
    // list ends above the walk-through and never runs the page down. Two rows of the columns'
    // grid, as the figure's surface is, so the head and the card start level with its own,
    // in one column held to the sidebar's width, so a long file name never widens the card.
    <div
      data-testid="define-sidebar"
      className="flex flex-col lg:row-span-2 lg:grid lg:grid-cols-[minmax(0,1fr)] lg:grid-rows-subgrid"
    >
      <div
        data-testid="define-sidebar-head"
        className="mb-2.5 flex items-center"
      >
        {head}
      </div>
      <Panel
        data-testid="define-list-card"
        pad="tight"
        className={cn(
          "flex w-full flex-col gap-2 lg:self-start",
          DEFINE_COLUMN_BOUND,
        )}
      >
        <FigureNavigator
          taskId={taskId}
          figures={figures}
          layout={layout}
          statuses={statuses}
          selectedId={asideId === undefined ? selectedId : ""}
          onSelect={onSelect}
        />
        <DeletedNavList
          deleted={listing.deleted ?? []}
          selectedId={asideId}
          onSelect={onSelectAside}
          {...acts}
        />
      </Panel>
    </div>
  );
}

/**
 * The sidebar and the working column: the open figure's surface, or a deleted figure opened
 * from the navigator's fold, read-only in its place.
 */
function DefineColumns({
  taskId,
  listing,
  set,
  focus,
  children,
}: {
  taskId: string | undefined;
  listing: FigureListResponse;
  set: ReturnType<typeof useFigureDefinitions>;
  focus: ReturnType<typeof useDefineFocus>;
  children: ReactNode;
}) {
  const aside = (listing.deleted ?? []).find(
    (figure) => figure.file_id === focus.asideId,
  );
  const acts = setAsideActs(set, focus);
  return (
    // Three rows: the heads (Figure Settings, the figure's heading), then the figure list,
    // the drawing and the elements, then the boxes the attorney writes in, across both
    // columns. The sidebar and the surface take the first two as subgrids, so they line up.
    <div
      data-testid="define-columns"
      className="mt-[18px] grid items-start gap-x-[18px] lg:grid-cols-[200px_1fr]"
    >
      <DefineSidebar
        taskId={taskId}
        listing={listing}
        definitions={set.definitions}
        selectedId={focus.showing?.file_id ?? ""}
        onSelect={focus.open}
        asideId={aside?.file_id}
        onSelectAside={focus.openAside}
        acts={acts}
        head={<SetSettings set={set} drawn={focus.showing} />}
      />
      {aside === undefined ? (
        children
      ) : (
        // Across both rows, so a tall banner never stretches the list's head.
        <div className="lg:row-span-2">
          <SetAsideFigureView figure={aside} {...acts} />
        </div>
      )}
    </div>
  );
}

/**
 * Which numeral each element carries, projected from the set.
 *
 * Registry order and the convention are the only inputs the projection reads.
 * Title edits, pin moves, and connector settings must not walk the set again.
 */
function useAssignedNumerals(
  definitions: ReturnType<typeof useFigureDefinitions>["definitions"],
): Map<string, string> {
  const registry = definitions.registry;
  const convention = definitions.settings.numbering_convention;
  return useMemo(
    () =>
      numeralsOf({ registry, settings: { numbering_convention: convention } }),
    [registry, convention],
  );
}

/**
 * The first drawing, in the order the set is laid out, that pins the component or, for a
 * sub-category, one of its members: where the Terminology panel's link for it lands.
 */
function firstPinning(
  figures: readonly FigureResponse[],
  definitions: Definitions,
  elementId: string,
): string | undefined {
  const named = new Set([
    elementId,
    ...definitions.registry
      .filter((row) => row.group_id === elementId)
      .map((row) => row.element_id),
  ]);
  return figures.find((figure) =>
    definitions.figures
      .find((row) => row.file_id === figure.file_id)
      ?.instances?.some((pin) => named.has(pin.element_id)),
  )?.file_id;
}

/**
 * Sent here by the Terminology panel: the component's drawing opens, its row
 * shows, and Edit puts the attorney in its title.
 *
 * The row is drawn only once the opened drawing renders, so the arrival waits
 * for it: it is held until a commit draws the row, rather than tried once on
 * the next frame, which under load can run before the drawing is drawn.
 * Opening the drawing is itself a render, so a ref is enough to carry it there.
 */
function useComponentArrival(
  ready: boolean,
  figures: readonly FigureResponse[],
  definitions: Definitions,
  open: (fileId: string) => void,
) {
  const arrivingRef = useRef<TermTarget | null>(null);
  useTermArrival(ready, (target) => {
    const fileId = firstPinning(figures, definitions, target.recordId);
    if (fileId === undefined) return;
    open(fileId);
    arrivingRef.current = target;
  });
  useEffect(() => {
    const target = arrivingRef.current;
    if (target === null) return;
    const row = document.getElementById(registryRowId(target.recordId));
    // Not drawn yet: this runs again after the commit that draws it.
    if (row === null) return;
    arrivingRef.current = null;
    arriveAt(row.id);
    if (target.edit) row.querySelector<HTMLElement>("[role=combobox]")?.focus();
  });
}

/** The open drawing's own row of the set, if it has one yet. */
function figureRow(rows: readonly FigureRow[], fileId: string | undefined) {
  return rows.find((row) => row.file_id === fileId);
}

/**
 * Describe each figure, and turn numbering on for the ones that need it.
 *
 * Step two of the figure engine. The drawings arrive already ordered and
 * labelled from step one, so this screen works one at a time rather than as a
 * list, which is what the two-column layout is for.
 */
export function FiguresDefinePane() {
  const { taskId } = useParams<{ taskId: string }>();
  const { data } = useFigures(taskId);
  // The whole set rather than one drawing's row, because numbering is shared: what a pin
  // here is called, and which other drawings carry it, are answers only the set holds. The
  // hook's view already folds in every change the store has not yet acknowledged.
  const set = useFigureDefinitions(taskId);
  const retryClip = useClipTranscription(taskId);
  const { send: uploadClip, pending: pendingClips } = useWalkthroughClipUpload(
    taskId,
    set.attachWalkthroughClip,
    data?.walkthrough_clips,
  );
  // Absent until the read answers, which is why the empty state below waits for
  // `data` rather than for an empty list: they are the same value here and only
  // one of them means the task has no drawings.
  const figures = data?.figures ?? [];
  // Selection is held here rather than inside the drawing, because the element table
  // highlights whichever pin is selected and the two surfaces have to agree on which.
  const focus = useDefineFocus(figures);
  const { showing, picked, select } = focus;
  const brief = useFigureBrief(taskId, showing?.file_id, data?.brief_run);
  useComponentArrival(data !== undefined, figures, set.definitions, focus.open);
  const selected = livePlacement(set.definitions, showing?.file_id, picked);
  const definition = figureRow(set.definitions.figures, showing?.file_id);
  const assigned = useAssignedNumerals(set.definitions);

  return (
    <StagePage
      guide="figures-define"
      testId="figures-define-pane"
      width="wide"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("figures")} · Define`}
      title="Define & Describe Figures"
      lede="One figure at a time. Every figure, numbered or not, needs your own account of what it shows; it is captured here and held, not written up yet. Numbering is shared across the set: define a component once, re-use its number anywhere."
    >
      {data === undefined ? null : showing === undefined &&
        (data.deleted ?? []).length === 0 ? (
        <NothingToDefine />
      ) : (
        <DefineColumns taskId={taskId} listing={data} set={set} focus={focus}>
          {showing === undefined ? (
            <NothingToDefine />
          ) : (
            <FigureDefineSurface
              figure={showing}
              figures={figures}
              definitions={set.definitions}
              assigned={assigned}
              selected={selected}
              numbered={definition?.numbered ?? false}
              priorArt={definition?.prior_art ?? false}
              removable={canRemoveNumbering(definition)}
              clips={heldClips(data, pendingClips, definition)}
              {...walkthroughActs(
                set,
                showing.file_id,
                uploadClip,
                retryClip,
                data.walkthrough_clips,
              )}
              walkthroughAccepted={definition?.walkthrough_accepted ?? false}
              groupTitle={groupTitleOf(data.layout, showing.file_id)}
              brief={brief}
              {...figureActs(set, showing)}
              onSelect={select}
              onPlace={(pin, tip, elementId) => {
                set.place(showing.file_id, { pin, tip, elementId });
              }}
              onRemovePin={(placement) => {
                // Nothing to do about the selection here either: the pin leaves the set and the
                // lookup above stops finding it.
                set.removePin(showing.file_id, placement);
              }}
              onRetitle={set.retitle}
              servedTitles={titlesOf(set.served)}
              onConfirm={set.confirm}
              onReuse={set.reuseElement}
              {...nestingActs(set, assigned)}
              edits={markEdits(set, showing.file_id)}
            />
          )}
        </DefineColumns>
      )}

      <StepLinks taskId={taskId} />
    </StagePage>
  );
}
